import { createContext, useReducer, useEffect, useRef } from "react";
import api from "../api";
import {
  isTokenExpired,
  saveTokenExpiry,
  clearStoredSession,
  redirectToLoginExpired,
  scheduleTokenExpiry,
} from "../utils/tokenExpiry";

export const AuthContext = createContext();

// Safe JSON parsing to prevent crash on invalid JSON
const getStoredUser = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Failed to parse user from localStorage:", error);
    return null;
  }
};

const getStoredToken = () => {
  try {
    return localStorage.getItem("token") || null;
  } catch (error) {
    console.error("Failed to retrieve token from localStorage:", error);
    return null;
  }
};

// A token that already expired while the app was closed is dropped before
// the first render, so no screen ever renders for (or fires requests as) a
// dead session.
const getInitialState = () => {
  const token = getStoredToken();
  if (token && isTokenExpired(token)) {
    clearStoredSession();
    return { user: null, token: null };
  }
  return { user: getStoredUser(), token };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      // Save to localStorage when login is successful
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      localStorage.setItem("token", action.payload.token);
      saveTokenExpiry(action.payload);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
      };
    case "LOGOUT":
      clearStoredSession();
      return {
        user: null,
        token: null,
      };
    case "SYNC_USER": {
      // Merge (not replace): /api/auth/me only returns the trusted, DB-driven
      // fields (id/name/email/role_id/role_name/unit_id). The cached user
      // object may carry additional fields from login (username, role,
      // company_id, company_name) that routing/nav still depend on — a full
      // replace would silently null those out on every page refresh.
      const mergedUser = { ...state.user, ...action.payload.user };
      localStorage.setItem("user", JSON.stringify(mergedUser));
      return {
        ...state,
        user: mergedUser,
      };
    }
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);
  const hasValidatedSession = useRef(false);

  // Session-validation source: on first load, if a token is already cached,
  // confirm it's still valid (and re-sync role/user fields from the DB) via
  // GET /api/auth/me. A 401 here is handled centrally by the api.js response
  // interceptor (clears storage + redirects to /login); any other error
  // (e.g. a transient network issue) intentionally leaves the existing
  // cached session alone so it doesn't log users out unnecessarily.
  useEffect(() => {
    if (hasValidatedSession.current) return;
    hasValidatedSession.current = true;

    if (!state.token) return;

    api
      .get("/auth/me")
      .then((res) => {
        if (res?.data?.user) {
          dispatch({ type: "SYNC_USER", payload: { user: res.data.user } });
        }
      })
      .catch(() => {
        // Handled by the response interceptor when it's a real 401;
        // otherwise, do nothing and keep the existing cached session.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Token expiry: log out the moment the token expires, even on an idle
  // tab. Timers can fire late after the machine sleeps, so the check is
  // repeated whenever the tab becomes visible again.
  useEffect(() => {
    if (!state.token) return undefined;
    const expire = () => {
      dispatch({ type: "LOGOUT" });
      redirectToLoginExpired();
    };
    const cancel = scheduleTokenExpiry(state.token, expire);
    const onVisible = () => {
      if (document.visibilityState === "visible" && isTokenExpired(state.token)) expire();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancel();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [state.token]);

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};
